import { useState } from 'react'
import type { ReactNode } from 'react'
import { Pencil } from 'lucide-react'

import type { EditableFieldDescription } from '@fit-editor/fit-core'

import type { FieldCellContext } from '@/components/data/field-cell-renderer'
import { FieldEditDialog } from '@/components/data/field-edit-dialog'
import { messageInstanceLabel } from '@/lib/field-edit'

export interface EditableFieldCellProps {
  context: FieldCellContext
  description: EditableFieldDescription
  messagesKey: string
  children: ReactNode
}

/**
 * A value cell with an edit affordance: a small pencil button that is
 * always in the tab order (keyboard reachable), visible on hover/focus with
 * a pointer, and always visible on touch screens (no hover there). Opens
 * `FieldEditDialog`.
 */
export function EditableFieldCell({
  context,
  description,
  messagesKey,
  children,
}: EditableFieldCellProps) {
  const [open, setOpen] = useState(false)
  const label = `${context.value != null ? 'Edit' : 'Set'} ${context.fieldId} of ${messageInstanceLabel(messagesKey, context.messageIndex)}`

  return (
    <span className="group/cell inline-flex items-center gap-1">
      <span>{children}</span>
      <button
        type="button"
        aria-label={label}
        title={label}
        onClick={() => setOpen(true)}
        className="text-muted-foreground hover:bg-accent hover:text-accent-foreground focus-visible:ring-ring inline-flex h-5 w-5 shrink-0 items-center justify-center rounded-sm opacity-0 outline-none group-hover/cell:opacity-100 focus-visible:opacity-100 focus-visible:ring-2 pointer-coarse:opacity-100"
        data-testid="edit-field-button"
      >
        <Pencil className="h-3 w-3" aria-hidden="true" />
      </button>
      {open && (
        <FieldEditDialog
          context={context}
          description={description}
          messagesKey={messagesKey}
          onClose={() => setOpen(false)}
        />
      )}
    </span>
  )
}
